import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Build as BuildIcon,
  Schedule,
  CheckCircle,
  LocationOn,
  AccessTime,
  Person,
  Warning,
  Info,
  Assignment,
  Group,
  Notes,
  CloudQueue,
  Star,
  MoreVert,
  Delete,
  PlayArrow,
  Pause,
  Stop,
  Phone,
  Email,
  CalendarToday,
  Timer,
  Description,
  BugReport,
  Build,
} from '@mui/icons-material';

interface ServiceRequest {
  _id: string;
  customer: {
    id: string;
    name: string;
    contactPerson: {
      firstName: string;
      lastName: string;
      title?: string;
    };
    email: string;
    phone: string;
  };
  project: {
    id: string;
    name: string;
    completedDate: string;
  };
  installer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  issueType: string;
  priority: string;
  status: string;
  description: string;
  reportedDate: string;
  scheduledDate?: string;
  completedDate?: string;
  estimatedDuration: number;
  actualDuration?: number;
  location: string;
  assignedTechnician: {
    id: string;
    name: string;
    email: string;
  };
  resolution?: string;
  partsNeeded: string[];
  warrantyCovered: boolean;
  customerSatisfaction?: number;
  createdAt: string;
  updatedAt: string;
}

const ServiceRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [serviceRequest, setServiceRequest] = useState<ServiceRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<ServiceRequest>>({});
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<string>('');

  useEffect(() => {
    fetchServiceRequest();
  }, [id]);

  const fetchServiceRequest = async () => {
    try {
      setLoading(true);
      // Mock data - replace with actual API call
      const mockServiceRequest: ServiceRequest = {
        _id: id || '1',
        customer: {
          id: '1',
          name: 'Elite Lighting Solutions',
          contactPerson: {
            firstName: 'John',
            lastName: 'Doe',
            title: 'CEO'
          },
          email: 'john.doe@elitels.com',
          phone: '+1-555-0100'
        },
        project: {
          id: '1',
          name: 'Luxury Home LED Installation',
          completedDate: '2024-09-15T16:30:00Z'
        },
        installer: {
          id: '1',
          name: 'Mike Rodriguez',
          email: 'mike.r@bossolighting.com',
          phone: '+1-555-0200'
        },
        issueType: 'malfunction',
        priority: 'high',
        status: 'in_progress',
        description: 'LED strips in the living room are flickering intermittently. Customer reports the issue started 2 days ago and is getting worse.',
        reportedDate: '2024-10-20T09:00:00Z',
        scheduledDate: '2024-10-22T10:00:00Z',
        estimatedDuration: 3,
        actualDuration: 0,
        location: '123 Oak Street, Beverly Hills, CA',
        assignedTechnician: {
          id: '2',
          name: 'Alex Chen',
          email: 'alex.c@bossolighting.com'
        },
        partsNeeded: ['LED Controller', 'Power Supply Unit'],
        warrantyCovered: true,
        createdAt: '2024-10-20T09:00:00Z',
        updatedAt: '2024-10-20T14:30:00Z'
      };
      
      setServiceRequest(mockServiceRequest);
      setEditData(mockServiceRequest);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch service request details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      // Mock save - replace with actual API call
      console.log('Saving service request:', editData);
      setServiceRequest(prev => prev ? { ...prev, ...editData } : null);
      setEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save service request');
    }
  };

  const handleCancel = () => {
    setEditData(serviceRequest || {});
    setEditing(false);
  };

  const handleStatusChange = async () => {
    try {
      // Mock status change - replace with actual API call
      console.log('Changing status to:', newStatus);
      setServiceRequest(prev => prev ? { ...prev, status: newStatus } : null);
      setStatusDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to change status');
    }
  };

  const handleDelete = async () => {
    try {
      // Mock delete - replace with actual API call
      console.log('Deleting service request:', id);
      navigate('/services');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete service request');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'open': return 'info';
      case 'cancelled': return 'error';
      case 'on_hold': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'in_progress': return <BuildIcon />;
      case 'open': return <Schedule />;
      case 'cancelled': return <Cancel />;
      case 'on_hold': return <Pause />;
      default: return <Schedule />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getIssueTypeColor = (issueType: string) => {
    switch (issueType) {
      case 'malfunction': return 'error';
      case 'warranty': return 'warning';
      case 'maintenance': return 'info';
      case 'upgrade': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#D4AF37' }} />
      </Box>
    );
  }

  if (error || !serviceRequest) {
    return (
      <Box sx={{ backgroundColor: '#1A1A1A', minHeight: '100vh', p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Service request not found'}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/services')}
          sx={{ backgroundColor: '#D4AF37', color: '#000000' }}
        >
          Back to Services
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#1A1A1A', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => navigate('/services')}
            sx={{ color: '#D4AF37' }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ color: '#D4AF37', fontWeight: 700 }}>
            Service Request #{serviceRequest._id}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {!editing ? (
            <>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={handleEdit}
                sx={{ color: '#D4AF37', borderColor: '#D4AF37' }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={getStatusIcon(serviceRequest.status)}
                onClick={() => {
                  setNewStatus(serviceRequest.status);
                  setStatusDialogOpen(true);
                }}
                sx={{ color: '#D4AF37', borderColor: '#D4AF37' }}
              >
                Change Status
              </Button>
              <IconButton
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ color: '#F44336' }}
              >
                <Delete />
              </IconButton>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{ backgroundColor: '#D4AF37', color: '#000000' }}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                sx={{ color: '#F44336', borderColor: '#F44336' }}
              >
                Cancel
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Status and Priority Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                {getStatusIcon(serviceRequest.status)}
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Status</Typography>
                  <Chip
                    label={serviceRequest.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(serviceRequest.status) as any}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <Star sx={{ color: '#D4AF37' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Priority</Typography>
                  <Chip
                    label={serviceRequest.priority.toUpperCase()}
                    color={getPriorityColor(serviceRequest.priority) as any}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2}>
                <BugReport sx={{ color: '#D4AF37' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#FFFFFF' }}>Issue Type</Typography>
                  <Chip
                    label={serviceRequest.issueType.toUpperCase()}
                    color={getIssueTypeColor(serviceRequest.issueType) as any}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            '& .MuiTab-root': { color: '#AAAAAA' },
            '& .Mui-selected': { color: '#D4AF37' },
            '& .MuiTabs-indicator': { backgroundColor: '#D4AF37' },
          }}
        >
          <Tab label="Overview" />
          <Tab label="Customer & Project" />
          <Tab label="Team & Assignment" />
          <Tab label="Resolution & Parts" />
        </Tabs>

        <CardContent>
          {/* Overview Tab */}
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Service Details
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Description
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {editing ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={editData.description || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                        sx={{
                          '& .MuiInputBase-input': { color: '#FFFFFF' },
                          '& .MuiOutlinedInput-root': { borderColor: '#555555' },
                        }}
                      />
                    ) : (
                      serviceRequest.description
                    )}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Location
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn fontSize="small" />
                    {editing ? (
                      <TextField
                        fullWidth
                        value={editData.location || ''}
                        onChange={(e) => setEditData(prev => ({ ...prev, location: e.target.value }))}
                        sx={{ '& .MuiInputBase-input': { color: '#FFFFFF' } }}
                      />
                    ) : (
                      serviceRequest.location
                    )}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Warranty Coverage
                  </Typography>
                  <Chip
                    label={serviceRequest.warrantyCovered ? 'Covered' : 'Not Covered'}
                    color={serviceRequest.warrantyCovered ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Timeline
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Reported Date
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday fontSize="small" />
                    {formatDate(serviceRequest.reportedDate)}
                  </Typography>
                </Box>
                {serviceRequest.scheduledDate && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                      Scheduled Date
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule fontSize="small" />
                      {formatDate(serviceRequest.scheduledDate)}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Estimated Duration
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Timer fontSize="small" />
                    {serviceRequest.estimatedDuration} hours
                  </Typography>
                </Box>
                {serviceRequest.actualDuration && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                      Actual Duration
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Timer fontSize="small" />
                      {serviceRequest.actualDuration} hours
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}

          {/* Customer & Project Tab */}
          {tabValue === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Customer Information
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Company Name
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {serviceRequest.customer.name}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Contact Person
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {serviceRequest.customer.contactPerson.firstName} {serviceRequest.customer.contactPerson.lastName}
                    {serviceRequest.customer.contactPerson.title && ` - ${serviceRequest.customer.contactPerson.title}`}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Contact Information
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Email fontSize="small" />
                    {serviceRequest.customer.email}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone fontSize="small" />
                    {serviceRequest.customer.phone}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Project Information
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Project Name
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {serviceRequest.project.name}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#AAAAAA', mb: 1 }}>
                    Completion Date
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle fontSize="small" />
                    {formatDate(serviceRequest.project.completedDate)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Team & Assignment Tab */}
          {tabValue === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Original Installer
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar sx={{ backgroundColor: '#D4AF37', color: '#000000' }}>
                    <Person />
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                      {serviceRequest.installer.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                      {serviceRequest.installer.email}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                      {serviceRequest.installer.phone}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Assigned Technician
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar sx={{ backgroundColor: '#D4AF37', color: '#000000' }}>
                    <Build />
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                      {serviceRequest.assignedTechnician.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                      {serviceRequest.assignedTechnician.email}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Resolution & Parts Tab */}
          {tabValue === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Resolution Notes
                </Typography>
                {editing ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={editData.resolution || ''}
                    onChange={(e) => setEditData(prev => ({ ...prev, resolution: e.target.value }))}
                    sx={{
                      '& .MuiInputBase-input': { color: '#FFFFFF' },
                      '& .MuiOutlinedInput-root': { borderColor: '#555555' },
                    }}
                  />
                ) : (
                  <Paper sx={{ backgroundColor: '#1A1A1A', p: 2, border: '1px solid #333333' }}>
                    <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                      {serviceRequest.resolution || 'No resolution notes yet.'}
                    </Typography>
                  </Paper>
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Parts Needed
                </Typography>
                <List>
                  {serviceRequest.partsNeeded.map((part, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <BuildIcon sx={{ color: '#D4AF37' }} />
                      </ListItemIcon>
                      <ListItemText primary={part} sx={{ color: '#FFFFFF' }} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              {serviceRequest.customerSatisfaction && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                    Customer Satisfaction
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h4" sx={{ color: '#D4AF37' }}>
                      {serviceRequest.customerSatisfaction}/5
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          sx={{
                            color: i < serviceRequest.customerSatisfaction! ? '#D4AF37' : '#666666',
                            fontSize: 24
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            border: '1px solid #333333',
            color: '#FFFFFF',
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37' }}>Change Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel sx={{ color: '#AAAAAA' }}>New Status</InputLabel>
            <Select
              value={newStatus}
              label="New Status"
              onChange={(e) => setNewStatus(e.target.value)}
              sx={{ color: '#FFFFFF' }}
            >
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="on_hold">On Hold</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setStatusDialogOpen(false)} 
            sx={{ color: '#FFFFFF' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleStatusChange} 
            autoFocus 
            sx={{ color: '#D4AF37' }}
          >
            Change Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#1A1A1A',
            border: '1px solid #333333',
            color: '#FFFFFF',
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#FFFFFF' }}>
            Are you sure you want to delete this service request? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            sx={{ color: '#FFFFFF' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            autoFocus 
            sx={{ color: '#F44336' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceRequestDetail;

